import { lumi } from '@/lib/lumi'
import { useState, useCallback } from 'react'
import type { Product } from '@/types/product'

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchProducts = useCallback(async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    try {
      const { list, total } = await lumi.entities.products.list(options)
      setProducts(list)
      setTotal(total)
      console.log('Products fetched:', list)
    } catch (error) {
      console.error('Fetch products failed:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchProductBySlug = useCallback(async (slug: string): Promise<Product | null> => {
    try {
      const { list } = await lumi.entities.products.list({
        filter: { slug },
        limit: 1
      })
      return list.length > 0 ? list[0] : null
    } catch (error) {
      console.error('Fetch product by slug failed:', error)
      return null
    }
  }, [])

  const fetchFeaturedProducts = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.products.list({
        filter: { featured: true },
        sort: { createdAt: -1 },
        limit: 6
      })
      setProducts(list)
      console.log('Featured products fetched:', list)
    } catch (error) {
      console.error('Fetch featured products failed:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchProductsByCategory = useCallback(async (category: string) => {
    setLoading(true)
    try {
      const { list, total } = await lumi.entities.products.list({
        filter: { category },
        sort: { createdAt: -1 }
      })
      setProducts(list)
      setTotal(total)
      console.log(`Products in category ${category} fetched:`, list)
    } catch (error) {
      console.error('Fetch products by category failed:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    products,
    total,
    loading,
    fetchProducts,
    fetchProductBySlug,
    fetchFeaturedProducts,
    fetchProductsByCategory
  }
}
